import {
  CommentScopeType,
  DashboardState,
  ESdChangeReason,
  ICommentDto,
  IUser,
  IUserInfoDto
} from "@kuika/kuika-cl-model";
import { Guid } from "guid-typescript";
import React from "react";
import { IComments } from "../../../../models/interface/comment";
import { CommentService } from "../../../../services/comment-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import {
  DirectoryMember,
  getMembers,
  loadPeopleDirectory,
  subscribePeople
} from "../../../management/board/people-directory";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import { KuikaSkeletonList } from "../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaTabContent from "../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import { CanvasCommentPins } from "./canvas-comment-pins";
import { setOpenCount } from "./comment-count-store";
import { CommentReactions } from "./comment-reactions";
import { CommentThreadCard } from "./comment-thread-card";
import "./comments-panel.scss";
import {
  buildPinNumbers,
  convertCommentsDtoToInterface,
  isOpenMentionOf,
  PAGE_SCOPE_KUIKA_ID,
  toggleReactionOnComment
} from "./comments-panel-logic";
import {
  CommentFilter,
  CommentSort,
  ICommentsPanelProps,
  ICommentsPanelState,
  IFilterCounts,
  IThreadViewModel,
  NewCommentScope,
  threadKey
} from "./comments-panel-types";
import { MentionTextarea } from "./mention-textarea";

/** The filter tabs in their on-screen order, so a list slides in from the side its tab lies on. */
const COMMENT_FILTER_ORDER: string[] = [
  CommentFilter.All,
  CommentFilter.Open,
  CommentFilter.Resolved,
  CommentFilter.Mentions
];

// ─── Main CommentsPanel class component ────────────────────────────────────────

class CommentsPanel extends PureSdComponent<
  ICommentsPanelProps & IWithSDProps & IWithContextProp,
  ICommentsPanelState
> {
  private mounted: boolean = false;
  // Monotonic token so a slow in-flight reload can't overwrite a newer one's result.
  private reloadSeq: number = 0;
  // Optimistic comments whose server id hasn't arrived; react/delete/edit are ignored until the real id reconciles.
  private pendingCommentIds: Set<string> = new Set<string>();
  private unsubscribePeople: (() => void) | null = null;
  private fileInputRef: React.RefObject<HTMLInputElement | null> = React.createRef<HTMLInputElement | null>();

  constructor(props: ICommentsPanelProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      threads: [],
      allComments: [],
      isLoading: true,
      hasError: false,
      // Open unresolved comments first — that's what a reviewer usually needs to act on.
      filter: CommentFilter.Open,
      query: "",
      sort: CommentSort.Newest,
      selectedThreadId: "",
      draftNew: "",
      // Default to page scope: it's the neutral choice and never lands on a disabled composer
      // (component scope needs a component selected). Users switch to component when they mean to.
      newScope: NewCommentScope.Page,
      replyingThreadId: "",
      draftReply: "",
      editingCommentId: "",
      draftEdit: "",
      openMenuCommentId: "",
      expandedThreadIds: [],
      pendingAttachmentUrl: "",
      pendingAttachmentName: ""
    };
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  async componentDidMount(): Promise<void> {
    this.mounted = true;
    this.unsubscribePeople = subscribePeople(this.handleDirectoryUpdate);
    void loadPeopleDirectory();
    if (this.props.sd?.sdId) {
      await this.reloadComments();
    }
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleDocumentKeyDown);
  }

  componentWillUnmount(): void {
    this.mounted = false;
    if (this.unsubscribePeople) this.unsubscribePeople();
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
  }

  async componentDidUpdate(prevProps: ICommentsPanelProps & IWithSDProps & IWithContextProp): Promise<void> {
    if (prevProps.sd?.sdId !== this.props.sd?.sdId && this.props.sd?.sdId) {
      // New screen — clear per-screen transient UI so a half-typed draft, an open edit,
      // or a selection pointing at the previous screen's comment doesn't leak over.
      this.setState({
        selectedThreadId: "",
        replyingThreadId: "",
        editingCommentId: "",
        draftNew: "",
        draftReply: "",
        draftEdit: "",
        pendingAttachmentUrl: "",
        pendingAttachmentName: "",
        expandedThreadIds: [],
        openMenuCommentId: ""
      });
      await this.reloadComments();
    }
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return (
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock
    );
  };

  // ─── Directory update ───────────────────────────────────────────────────────

  private handleDirectoryUpdate = (): void => {
    if (this.mounted) this.forceUpdate();
  };

  // ─── Data loading ────────────────────────────────────────────────────────────

  // `silent` swaps data in place without a "Loading…" blank — used after every mutation; only initial/screen-switch loads show the loading state.
  private reloadComments = async (silent: boolean = false): Promise<void> => {
    if (!this.mounted) return;
    const sdId: string | undefined = this.props.sd?.sdId;
    // The /comments endpoint takes a `Guid`; a transient non-Guid sdId (e.g. hard-refresh with the
    // panel open, before the editor resolves the screen) would 400. Skip until a real screen is
    // loaded — componentDidUpdate reloads once sdId becomes a Guid. Keep the current isLoading so
    // the panel shows its spinner during that brief window instead of flashing an error/empty.
    if (!sdId || !Guid.isGuid(sdId)) return;
    const seq: number = ++this.reloadSeq;
    if (!silent) this.setState({ isLoading: true });
    try {
      const res = await CommentService.GetAllByScreenDesignId(sdId);
      // A newer reload started while this one was in flight — discard this stale result.
      if (!this.mounted || seq !== this.reloadSeq) return;
      // The service swallows request failures and returns the error object, so a
      // missing 200 status is our only signal that the load did not succeed.
      const isSuccess: boolean = Boolean(res) && res.status === 200;
      if (isSuccess) {
        const threads: IComments[] = convertCommentsDtoToInterface(res.data);
        const openCount: number = threads.filter((t: IComments) => !t.comments[0]?.isResolved).length;
        setOpenCount(openCount);
        this.setState({ threads, allComments: res.data, hasError: false });
      } else if (!silent) {
        // Only a non-silent (initial/screen-switch) failure shows the error state; a failed silent resync keeps the optimistic list.
        setOpenCount(0);
        this.setState({ threads: [], allComments: [], hasError: true });
      }
    } catch (e: unknown) {
      if (seq !== this.reloadSeq) return;
      console.error("Comments load error:", e);
      if (!silent && this.mounted) {
        setOpenCount(0);
        this.setState({ threads: [], allComments: [], hasError: true });
      }
    } finally {
      if (!silent && this.mounted && seq === this.reloadSeq) this.setState({ isLoading: false });
    }
  };

  // ─── Optimistic state helpers ────────────────────────────────────────────────
  // Mutations update local state immediately and only reload on failure, so actions feel instant.

  private currentUserAsAuthor(): IUser {
    const u: IUserInfoDto | undefined = this.props.context?.userInfo;
    return { id: u?.id ?? "", username: u?.username ?? "", firstName: u?.firstName ?? "", lastName: u?.lastName ?? "" };
  }

  private patchCommentInState(commentId: string, updater: (c: ICommentDto) => ICommentDto): void {
    this.setState((prev: ICommentsPanelState) => ({
      allComments: prev.allComments.map((c: ICommentDto) => (c.id === commentId ? updater(c) : c)),
      threads: prev.threads.map((t: IComments) =>
        t.comments.some((c: ICommentDto) => c.id === commentId)
          ? { ...t, comments: t.comments.map((c: ICommentDto) => (c.id === commentId ? updater(c) : c)) }
          : t
      )
    }));
  }

  private removeCommentFromState(commentId: string): void {
    this.pendingCommentIds.delete(commentId);
    this.setState((prev: ICommentsPanelState) => {
      const allComments: ICommentDto[] = prev.allComments.filter((c: ICommentDto) => c.id !== commentId);
      const threads: IComments[] = prev.threads
        .map((t: IComments) => ({ ...t, comments: t.comments.filter((c: ICommentDto) => c.id !== commentId) }))
        .filter((t: IComments) => t.comments.length > 0);
      setOpenCount(threads.filter((t: IComments) => !t.comments[0]?.isResolved).length);
      return { allComments, threads };
    });
  }

  private setThreadResolvedInState(rootCommentId: string, resolved: boolean): void {
    const resolver: IUser | undefined = resolved ? this.currentUserAsAuthor() : undefined;
    const resolvedAt: Date | undefined = resolved ? new Date() : undefined;
    this.setState((prev: ICommentsPanelState) => {
      const thread: IComments | undefined = prev.threads.find((t: IComments) =>
        t.comments.some((c: ICommentDto) => c.id === rootCommentId)
      );
      if (!thread) return null;
      const memberIds: Set<string> = new Set<string>(thread.comments.map((c: ICommentDto) => c.id));
      const patch = (c: ICommentDto): ICommentDto =>
        memberIds.has(c.id) ? { ...c, isResolved: resolved, resolvedByUser: resolver, resolvedAt } : c;
      const threads: IComments[] = prev.threads.map((t: IComments) =>
        t === thread ? { ...t, comments: t.comments.map(patch) } : t
      );
      setOpenCount(threads.filter((t: IComments) => !t.comments[0]?.isResolved).length);
      return { allComments: prev.allComments.map(patch), threads };
    });
  }

  // Insert a just-created comment instantly: replies join their thread; a new top-level comment opens a thread at order max+1 (matches backend NextOrder, keeps pin numbers correct).
  private insertCommentInState(newComment: ICommentDto): void {
    this.setState((prev: ICommentsPanelState) => {
      const groupKey: string = newComment.threadId ?? newComment.kuikaId;
      const existing: IComments | undefined = prev.threads.find(
        (t: IComments) => (t.threadId ?? t.kuikaId) === groupKey
      );
      let threads: IComments[];
      if (existing) {
        threads = prev.threads.map((t: IComments) => {
          if ((t.threadId ?? t.kuikaId) !== groupKey) return t;
          const comments: ICommentDto[] = [...t.comments, { ...newComment, order: t.order }].sort(
            (a: ICommentDto, b: ICommentDto) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
          return { ...t, comments, latestCreatedAt: newComment.createdAt };
        });
      } else {
        const order: number = prev.threads.reduce((m: number, t: IComments) => Math.max(m, t.order), 0) + 1;
        threads = [
          {
            kuikaId: newComment.kuikaId,
            order,
            comments: [{ ...newComment, order }],
            latestCreatedAt: newComment.createdAt,
            threadId: newComment.threadId,
            anchorFx: newComment.anchorX,
            anchorFy: newComment.anchorY
          },
          ...prev.threads
        ];
      }
      threads = [...threads].sort(
        (a: IComments, b: IComments) => new Date(b.latestCreatedAt).getTime() - new Date(a.latestCreatedAt).getTime()
      );
      setOpenCount(threads.filter((t: IComments) => !t.comments[0]?.isResolved).length);
      return { allComments: [...prev.allComments, newComment], threads };
    });
  }

  // ─── Component resolution ────────────────────────────────────────────────────

  private isComponentDeleted = (kuikaId: string): boolean => {
    if (kuikaId === PAGE_SCOPE_KUIKA_ID) return false;
    const di = this.props.sd.screenDesignDictionary[parseInt(kuikaId, 10)];
    return di === undefined;
  };

  private getComponentName = (kuikaId: string): string => {
    if (kuikaId === PAGE_SCOPE_KUIKA_ID) return "This page";
    const di = this.props.sd.screenDesignDictionary[parseInt(kuikaId, 10)];
    if (!di) return "";
    const def = this.props.sd.getComponentDefinition(di.item);
    const name: string = def?.name ?? "";
    return name === "div" ? "Screen" : name;
  };

  private getSelectedKuikaIds = (): string[] => {
    if (
      this.props.sd.dashboardState === DashboardState.dataDiagramScheme ||
      this.props.sd.dashboardState === DashboardState.workFlow
    ) {
      return ["-1"];
    }
    return this.props.sd.selectedKuikaIds ?? [];
  };

  private isSelectionDisabled = (): boolean => {
    const ids: string[] = this.getSelectedKuikaIds();
    return ids.length === 0 || ids[0] === "-1";
  };

  // ─── Build view models ───────────────────────────────────────────────────────

  // One thread → view model. Shared by the panel list (mapped + filtered) and the canvas pin
  // popover (single thread, shown regardless of the active filter).
  private buildViewModel = (thread: IComments): IThreadViewModel => {
    const root: ICommentDto = thread.comments[0];
    const replies: ICommentDto[] = thread.comments.slice(1);
    const isResolved: boolean = root?.isResolved === true;
    const anchorLabel: string = root?.anchorLabel ?? this.getComponentName(thread.kuikaId);
    const isDeleted: boolean = this.isComponentDeleted(thread.kuikaId);
    return {
      thread,
      root,
      replies,
      order: thread.order,
      isResolved,
      anchorLabel,
      isDeleted,
      componentName: this.getComponentName(thread.kuikaId)
    };
  };

  private isOpenMentionOfMe = (thread: IComments): boolean => isOpenMentionOf(thread, this.props.context?.userInfo?.id);

  /** The threads the list shows under `filter` — the active one by default, a leaving one during a swap. */
  private buildViewModels = (filter: CommentFilter = this.state.filter): IThreadViewModel[] => {
    const { threads, query, sort } = this.state;

    let viewModels: IThreadViewModel[] = threads.map(this.buildViewModel);

    // Filter
    if (filter === CommentFilter.Open) {
      viewModels = viewModels.filter((vm: IThreadViewModel) => !vm.isResolved);
    } else if (filter === CommentFilter.Resolved) {
      viewModels = viewModels.filter((vm: IThreadViewModel) => vm.isResolved);
    } else if (filter === CommentFilter.Mentions) {
      viewModels = viewModels.filter((vm: IThreadViewModel) => this.isOpenMentionOfMe(vm.thread));
    }

    // Search
    if (query.trim()) {
      const q: string = query.toLowerCase();
      viewModels = viewModels.filter(
        (vm: IThreadViewModel) =>
          vm.thread.comments.some((c: ICommentDto) => c.comment.toLowerCase().includes(q)) ||
          vm.anchorLabel.toLowerCase().includes(q)
      );
    }

    // Sort
    if (sort === CommentSort.Oldest) {
      viewModels = [...viewModels].sort(
        (a: IThreadViewModel, b: IThreadViewModel) =>
          new Date(a.thread.latestCreatedAt).getTime() - new Date(b.thread.latestCreatedAt).getTime()
      );
    }

    return viewModels;
  };

  private buildFilterCounts = (): IFilterCounts => {
    const { threads } = this.state;
    const all: number = threads.length;
    const open: number = threads.filter((t: IComments) => !t.comments[0]?.isResolved).length;
    const resolved: number = threads.filter((t: IComments) => t.comments[0]?.isResolved === true).length;
    const mentions: number = threads.filter((t: IComments) => this.isOpenMentionOfMe(t)).length;
    return { all, open, resolved, mentions };
  };

  // ─── Document click handler (close overflow menu) ─────────────────────────────

  private handleDocumentClick = (e: MouseEvent): void => {
    const target: EventTarget | null = e.target;
    if (!(target instanceof Element)) return;
    if (!target.closest(".comments-panel__menu") && !target.closest("[data-menu-trigger]")) {
      if (this.state.openMenuCommentId) this.setState({ openMenuCommentId: "" });
    }
  };

  private handleDocumentKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this.state.openMenuCommentId) {
      this.setState({ openMenuCommentId: "" });
    }
  };

  // ─── Filter/sort/search ──────────────────────────────────────────────────────

  private handleFilterChange = (value: string): void => {
    this.setState({ filter: value as CommentFilter });
  };

  private handleSearchChange = (
    _e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    value: string | number
  ): void => {
    this.setState({ query: String(value) });
  };

  private toggleSort = (): void => {
    this.setState((prev: ICommentsPanelState) => ({
      sort: prev.sort === CommentSort.Newest ? CommentSort.Oldest : CommentSort.Newest
    }));
  };

  // ─── Thread selection ────────────────────────────────────────────────────────

  private handleSelectThread = (threadKeyArg: string, kuikaId: string): void => {
    // Pins show every thread but the list is filtered/searched — switch to a revealing tab and drop a hiding query, then jump to the card.
    const thread: IComments | undefined = this.state.threads.find((t: IComments) => threadKey(t) === threadKeyArg);
    const nextFilter: CommentFilter = thread ? this.filterRevealing(thread) : this.state.filter;
    const nextQuery: string = thread && !this.queryMatchesThread(thread) ? "" : this.state.query;
    this.setState({ selectedThreadId: threadKeyArg, filter: nextFilter, query: nextQuery }, () =>
      this.scrollThreadCardIntoView(threadKeyArg)
    );
    if (!this.isComponentDeleted(kuikaId) && kuikaId !== PAGE_SCOPE_KUIKA_ID) {
      this.props.sd.setSelectedDisplayItem(kuikaId);
      this.scrollComponentIntoView(kuikaId);
    }
  };

  // Which filter tab reveals this thread: keep the current one if it already shows it, else fall to Resolved/Open matching the thread's state.
  private filterRevealing(thread: IComments): CommentFilter {
    const { filter } = this.state;
    const isResolved: boolean = thread.comments[0]?.isResolved === true;
    const hidden: boolean =
      (filter === CommentFilter.Open && isResolved) ||
      (filter === CommentFilter.Resolved && !isResolved) ||
      (filter === CommentFilter.Mentions && !this.isOpenMentionOfMe(thread));
    if (!hidden) return filter;
    return isResolved ? CommentFilter.Resolved : CommentFilter.Open;
  }

  private queryMatchesThread(thread: IComments): boolean {
    const q: string = this.state.query.trim().toLowerCase();
    if (!q) return true;
    const anchorLabel: string = thread.comments[0]?.anchorLabel ?? this.getComponentName(thread.kuikaId);
    return (
      thread.comments.some((c: ICommentDto) => c.comment.toLowerCase().includes(q)) ||
      anchorLabel.toLowerCase().includes(q)
    );
  }

  // Scroll the selected thread's card into view; `nearest` leaves an already-visible card put.
  private scrollThreadCardIntoView = (threadKeyArg: string): void => {
    const card: Element | null = document.querySelector(`[data-testid="comment-thread-${threadKeyArg}"]`);
    if (card) card.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  // Pan the canvas to an off-screen component; scoped to the iframe's scroll container so it never moves the outer designer document.
  private scrollComponentIntoView = (kuikaId: string): void => {
    const iframe: HTMLIFrameElement | null = document.getElementById(
      "kuika_iframeAgent_canvas"
    ) as HTMLIFrameElement | null;
    const doc: Document | null = iframe?.contentDocument ?? null;
    if (!doc) return;
    // A kuikaId can span nested nodes (incl. a 0×0 wrapper) — scroll the sized one; `nearest` only scrolls when off-screen so visible components don't jerk.
    let target: Element | null = null;
    const candidates: NodeListOf<Element> = doc.querySelectorAll(`[kuikaid="${kuikaId}"]`);
    for (const candidate of Array.from(candidates)) {
      const rect: DOMRect = candidate.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        target = candidate;
        break;
      }
    }
    if (target) target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  };

  private handleHoverThread = (kuikaId: string): void => {
    if (!this.isComponentDeleted(kuikaId) && kuikaId !== PAGE_SCOPE_KUIKA_ID) {
      this.props.sd.highlightHoveredItem(kuikaId);
    }
  };

  private handleToggleExpand = (kuikaId: string): void => {
    this.setState((prev: ICommentsPanelState) => {
      const isExpanded: boolean = prev.expandedThreadIds.includes(kuikaId);
      const expandedThreadIds: string[] = isExpanded
        ? prev.expandedThreadIds.filter((id: string) => id !== kuikaId)
        : [...prev.expandedThreadIds, kuikaId];
      return { expandedThreadIds };
    });
  };

  // ─── Menu ────────────────────────────────────────────────────────────────────

  private handleOpenMenu = (e: React.MouseEvent, commentId: string): void => {
    e.stopPropagation();
    this.setState((prev: ICommentsPanelState) => ({
      openMenuCommentId: prev.openMenuCommentId === commentId ? "" : commentId
    }));
  };

  private handleEdit = (comment: ICommentDto): void => {
    this.setState({ editingCommentId: comment.id, draftEdit: comment.comment, openMenuCommentId: "" });
  };

  private handleDelete = async (commentId: string): Promise<void> => {
    this.setState({ openMenuCommentId: "" });
    // Ignore deletes on a just-created comment until its real id is reconciled (see pendingCommentIds).
    if (this.pendingCommentIds.has(commentId)) return;
    // Optimistic remove; restore the previous state if the delete fails.
    const prevThreads: IComments[] = this.state.threads;
    const prevAll: ICommentDto[] = this.state.allComments;
    this.removeCommentFromState(commentId);
    const res = await CommentService.Delete(commentId);
    if ((!res || res.status !== 200) && this.mounted) {
      setOpenCount(prevThreads.filter((t: IComments) => !t.comments[0]?.isResolved).length);
      this.setState({ threads: prevThreads, allComments: prevAll });
    }
  };

  // ─── Edit ────────────────────────────────────────────────────────────────────

  private handleEditChange = (value: string): void => {
    this.setState({ draftEdit: value });
  };

  private submitEdit = async (): Promise<void> => {
    const { editingCommentId, draftEdit, allComments } = this.state;
    if (!editingCommentId || !draftEdit.trim()) return;
    // Ignore edits on a just-created comment until its real id is reconciled (see pendingCommentIds).
    if (this.pendingCommentIds.has(editingCommentId)) return;
    const commentObj: ICommentDto | undefined = allComments.find((c: ICommentDto) => c.id === editingCommentId);
    if (!commentObj) return;
    const mentionedUserIds: string[] = this.extractMentionedUserIds(draftEdit);
    const updated: ICommentDto = { ...commentObj, comment: draftEdit, mentionedUserIds, isEdited: true };
    this.setState({ editingCommentId: "", draftEdit: "" });
    // Optimistic: show the edited text immediately; resync only if the update fails.
    this.patchCommentInState(updated.id, (c: ICommentDto) => ({
      ...c,
      comment: draftEdit,
      mentionedUserIds,
      isEdited: true
    }));
    const res = await CommentService.Update(updated);
    if (!res || res.status !== 200) await this.reloadComments(true);
  };

  // ─── Reply ────────────────────────────────────────────────────────────────────

  private handleReplyChange = (value: string): void => {
    this.setState({ draftReply: value });
  };

  // Swap a locally-inserted comment's temporary id for the server-assigned one once the write
  // returns, so later edit/delete/react target the real row.
  private replaceCommentId(tempId: string, realId: string): void {
    this.pendingCommentIds.delete(tempId);
    if (!realId || realId === tempId) return;
    this.patchCommentInState(tempId, (c: ICommentDto) => ({ ...c, id: realId }));
  }

  private submitReply = async (): Promise<void> => {
    const { replyingThreadId, draftReply } = this.state;
    if (!replyingThreadId || !draftReply.trim()) return;
    const mentionedUserIds: string[] = this.extractMentionedUserIds(draftReply);
    // replyingThreadId is a thread KEY — resolve the parent so the reply carries its real kuikaId + threadId (null threadId → backend attaches by kuikaId).
    const parentThread: IComments | undefined = this.state.threads.find(
      (t: IComments) => threadKey(t) === replyingThreadId
    );
    const obj: ICommentDto = {
      id: "",
      comment: draftReply,
      kuikaId: parentThread?.kuikaId ?? replyingThreadId,
      threadId: parentThread?.threadId,
      kScreenDesign_Id: this.props.sd.sdId,
      createdAt: new Date(),
      isEdited: false,
      order: 0,
      mentionedUserIds,
      createdUser: { id: "", username: "", firstName: "", lastName: "" }
    };
    // Optimistic: show the reply instantly with a temp id, then reconcile the real id when the
    // write returns (or remove it on failure) — so it doesn't wait on the round-trip.
    const tempId: string = crypto.randomUUID();
    this.pendingCommentIds.add(tempId);
    this.insertCommentInState({ ...obj, id: tempId, createdUser: this.currentUserAsAuthor(), reactions: [] });
    this.setState({ draftReply: "", replyingThreadId: "" });
    const res = await CommentService.Create(obj);
    if (res && res.status === 200) {
      this.replaceCommentId(tempId, typeof res.data === "string" ? res.data : "");
    } else {
      this.removeCommentFromState(tempId);
    }
  };

  // ─── New comment composer ────────────────────────────────────────────────────

  // @mention autocomplete now lives inside MentionTextarea; the panel just tracks the draft text.
  private handleNewCommentChange = (value: string): void => {
    this.setState({ draftNew: value });
  };

  private handleScopeChange = (value: string): void => {
    this.setState({ newScope: value as NewCommentScope });
  };

  private submitNewComment = async (): Promise<void> => {
    const { draftNew, newScope, pendingAttachmentUrl, pendingAttachmentName } = this.state;
    if (!draftNew.trim()) return;

    let kuikaId: string = "";
    let scopeType: CommentScopeType = CommentScopeType.Component;
    let anchorLabel: string = "";

    if (newScope === NewCommentScope.Page) {
      kuikaId = PAGE_SCOPE_KUIKA_ID;
      scopeType = CommentScopeType.Page;
      anchorLabel = "This page";
    } else {
      const selectedIds: string[] = this.getSelectedKuikaIds();
      if (!selectedIds.length || selectedIds[0] === "-1") {
        return;
      }
      kuikaId = selectedIds[0];
      scopeType = CommentScopeType.Component;
      anchorLabel = this.getComponentName(kuikaId);
    }

    const mentionedUserIds: string[] = this.extractMentionedUserIds(draftNew);
    const isComponent: boolean = scopeType === CommentScopeType.Component;
    // Generate the thread id up-front so we can also select this thread after create
    // (the backend persists the client's id, so it survives the reload).
    const newThreadId: string = crypto.randomUUID();
    const obj: ICommentDto = {
      id: "",
      comment: draftNew,
      kuikaId,
      kScreenDesign_Id: this.props.sd.sdId,
      createdAt: new Date(),
      isEdited: false,
      order: 0,
      scopeType,
      anchorLabel,
      mentionedUserIds,
      attachmentUrl: pendingAttachmentUrl || undefined,
      attachmentName: pendingAttachmentName || undefined,
      // New top-level thread gets a fresh id; composer has no click point, so a
      // component comment anchors to the centre (page comments have no pin).
      threadId: newThreadId,
      anchorX: isComponent ? 0.5 : undefined,
      anchorY: isComponent ? 0.5 : undefined,
      createdUser: { id: "", username: "", firstName: "", lastName: "" }
    };

    // Optimistic: open the new thread instantly with a temp id, then reconcile the real id when
    // the write returns (or remove it on failure) — no waiting on the round-trip.
    const tempId: string = crypto.randomUUID();
    this.pendingCommentIds.add(tempId);
    const newComment: ICommentDto = { ...obj, id: tempId, createdUser: this.currentUserAsAuthor(), reactions: [] };
    this.insertCommentInState(newComment);
    // Reveal the just-added comment even if the active tab/search would hide it (same logic as a pin click), then scroll its card into view.
    const newThread: IComments = {
      kuikaId: obj.kuikaId,
      order: 0,
      comments: [newComment],
      latestCreatedAt: obj.createdAt,
      threadId: newThreadId
    };
    this.setState(
      {
        selectedThreadId: newThreadId,
        filter: this.filterRevealing(newThread),
        query: this.queryMatchesThread(newThread) ? this.state.query : "",
        draftNew: "",
        pendingAttachmentUrl: "",
        pendingAttachmentName: ""
      },
      () => this.scrollThreadCardIntoView(newThreadId)
    );
    const res = await CommentService.Create(obj);
    if (res && res.status === 200) {
      this.replaceCommentId(tempId, typeof res.data === "string" ? res.data : "");
    } else {
      this.removeCommentFromState(tempId);
    }
  };

  // ─── Canvas click-to-add (Figma-style) ───────────────────────────────────────
  // Invoked by CanvasCommentPins on a comment-mode click: creates a component-scoped comment anchored to that component.

  private handleCanvasAddComment = async (kuikaId: string, fx: number, fy: number, text: string): Promise<void> => {
    if (!text.trim()) return;
    const mentionedUserIds: string[] = this.extractMentionedUserIds(text);
    // Generate the thread id up-front so we can select this exact thread after create
    // (the backend persists the client's id, so it survives the reload).
    const newThreadId: string = crypto.randomUUID();
    const obj: ICommentDto = {
      id: "",
      comment: text,
      kuikaId,
      kScreenDesign_Id: this.props.sd.sdId,
      createdAt: new Date(),
      isEdited: false,
      order: 0,
      scopeType: CommentScopeType.Component,
      anchorLabel: this.getComponentName(kuikaId),
      mentionedUserIds,
      // New free-placed thread: fresh id, pin at the exact click fraction.
      threadId: newThreadId,
      anchorX: fx,
      anchorY: fy,
      createdUser: { id: "", username: "", firstName: "", lastName: "" }
    };
    // Optimistic: drop the pin + thread in instantly with a temp id, then reconcile the real id
    // when the write returns (or remove it on failure) — no waiting on the round-trip.
    const tempId: string = crypto.randomUUID();
    this.pendingCommentIds.add(tempId);
    const newComment: ICommentDto = { ...obj, id: tempId, createdUser: this.currentUserAsAuthor(), reactions: [] };
    this.insertCommentInState(newComment);
    // Reveal the new comment regardless of the active tab/search (same logic as a pin click), then
    // scroll its card into view.
    const newThread: IComments = {
      kuikaId: obj.kuikaId,
      order: 0,
      comments: [newComment],
      latestCreatedAt: obj.createdAt,
      threadId: newThreadId
    };
    this.setState(
      {
        selectedThreadId: newThreadId,
        filter: this.filterRevealing(newThread),
        query: this.queryMatchesThread(newThread) ? this.state.query : ""
      },
      () => this.scrollThreadCardIntoView(newThreadId)
    );
    const res = await CommentService.Create(obj);
    if (res && res.status === 200) {
      this.replaceCommentId(tempId, typeof res.data === "string" ? res.data : "");
    } else {
      this.removeCommentFromState(tempId);
    }
  };

  // ─── Resolve / Reopen ────────────────────────────────────────────────────────

  private handleResolve = async (rootCommentId: string): Promise<void> => {
    this.setThreadResolvedInState(rootCommentId, true);
    const res = await CommentService.Resolve(rootCommentId);
    if (!res || res.status !== 200) await this.reloadComments(true);
  };

  private handleReopen = async (rootCommentId: string): Promise<void> => {
    this.setThreadResolvedInState(rootCommentId, false);
    const res = await CommentService.Reopen(rootCommentId);
    if (!res || res.status !== 200) await this.reloadComments(true);
  };

  // ─── Reactions ───────────────────────────────────────────────────────────────

  private handleToggleReaction = async (commentId: string, emoji: string): Promise<void> => {
    // Ignore reactions on a just-created comment until its real id is reconciled (see pendingCommentIds).
    if (this.pendingCommentIds.has(commentId)) return;
    // Optimistic: toggle locally right away, then persist. On failure resync from the server.
    const currentUserId: string = this.props.context?.userInfo?.id ?? "";
    this.patchCommentInState(commentId, (c: ICommentDto) => toggleReactionOnComment(c, emoji, currentUserId));
    const res = await CommentService.ToggleReaction(commentId, emoji);
    if (!res || res.status !== 200) {
      await this.reloadComments(true);
    }
  };

  // ─── Attachment upload ────────────────────────────────────────────────────────

  private handleAttachClick = (): void => {
    this.fileInputRef.current?.click();
  };

  private handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file: File | undefined = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await CommentService.UploadAttachment(file);
      if (res && res.status === 200 && res.data) {
        this.setState({ pendingAttachmentUrl: res.data.url, pendingAttachmentName: res.data.name });
      }
    } catch {
      /* already handled by service */
    }
    // Reset file input so same file can be re-selected
    if (this.fileInputRef.current) this.fileInputRef.current.value = "";
  };

  // ─── @mention → user ids ──────────────────────────────────────────────────────
  // Autocomplete lives in MentionTextarea; the panel only resolves the "@Full Name" tokens the
  // composers produce back into user ids at submit time.
  private extractMentionedUserIds = (text: string): string[] => {
    // Longest name first + consume each match so a prefix name (e.g. "Ali" vs "Ali Veli") isn't falsely matched inside a longer one.
    const members: DirectoryMember[] = [...getMembers()].sort(
      (a: DirectoryMember, b: DirectoryMember) => b.name.length - a.name.length
    );
    const ids: string[] = [];
    let remaining: string = text;
    for (const member of members) {
      const token: string = `@${member.name}`;
      if (remaining.includes(token)) {
        ids.push(member.id);
        remaining = remaining.split(token).join(" ");
      }
    }
    return ids;
  };

  // ─── Composer ─────────────────────────────────────────────────────────────────

  private renderComposer(): React.ReactNode {
    const { draftNew, newScope, pendingAttachmentUrl, pendingAttachmentName } = this.state;
    const isComponentDisabled: boolean = this.isSelectionDisabled();

    const scopeOptions: string[] = [NewCommentScope.Page, NewCommentScope.Component];
    const scopeLabels: string[] = ["This page", "Selected component"];
    const tabOptions = scopeOptions.map((v: string, i: number) => ({
      value: v,
      label: scopeLabels[i]
    }));

    return (
      <div className="comments-panel__composer">
        <div className="comments-panel__composer-scope">
          <KuikaTabs options={tabOptions} value={newScope} onChange={this.handleScopeChange} size="sm" />
        </div>
        {newScope === NewCommentScope.Component && isComponentDisabled && (
          <div style={{ fontSize: 11, color: "var(--dark-6)", marginBottom: 6 }}>
            No component selected. Select a component on the canvas first.
          </div>
        )}
        <div className="comments-panel__composer-body">
          <MentionTextarea
            className="comments-panel__textarea"
            dataTestId="commentComposerInput"
            value={draftNew}
            onChange={this.handleNewCommentChange}
            onSubmit={(): void => void this.submitNewComment()}
            placeholder="Write a comment... (@ to mention)"
            rows={3}
            disabled={newScope === NewCommentScope.Component && isComponentDisabled}
          />
          {pendingAttachmentUrl && (
            <div className="comments-panel__attach-pending">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {pendingAttachmentName || "Attachment"}
              </span>
              <span
                className="comments-panel__attach-remove"
                onClick={(): void => this.setState({ pendingAttachmentUrl: "", pendingAttachmentName: "" })}
              >
                ×
              </span>
            </div>
          )}
          <div className="comments-panel__composer-actions">
            <button
              className="comments-panel__composer-icon-btn"
              onClick={this.handleAttachClick}
              type="button"
              title="Attach file"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
            </button>
            <input
              ref={this.fileInputRef as React.RefObject<HTMLInputElement>}
              type="file"
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
              style={{ display: "none" }}
              onChange={this.handleFileChange}
            />
            <KuikaButton
              type="primary"
              size="sm"
              disabled={!draftNew.trim() || (newScope === NewCommentScope.Component && isComponentDisabled)}
              onClick={(): void => {
                void this.submitNewComment();
              }}
              data-testid="comment-send-btn"
            >
              Send
            </KuikaButton>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main render ─────────────────────────────────────────────────────────────

  render(): React.ReactNode {
    const { isLoading, hasError, threads, filter, query, sort } = this.state;
    const viewModels: IThreadViewModel[] = this.buildViewModels();
    const counts: IFilterCounts = this.buildFilterCounts();
    // Build once per render; stable across filter/sort changes because it keys
    // off the full unfiltered thread list ordered by the backend `order` field.
    const pinNumbers: Map<string, number> = buildPinNumbers(threads);

    // One card, wired to every mutation handler — rendered both in the panel list and, identically,
    // inside the canvas pin popover (Figma-style: the same thread appears on its pin).
    const renderThreadCard = (vm: IThreadViewModel): React.ReactNode => (
      <CommentThreadCard
        key={threadKey(vm.thread)}
        vm={vm}
        pinNumbers={pinNumbers}
        currentUserId={this.props.context?.userInfo?.id}
        selectedThreadId={this.state.selectedThreadId}
        expandedThreadIds={this.state.expandedThreadIds}
        editingCommentId={this.state.editingCommentId}
        draftEdit={this.state.draftEdit}
        replyingThreadId={this.state.replyingThreadId}
        draftReply={this.state.draftReply}
        openMenuCommentId={this.state.openMenuCommentId}
        onSelectThread={this.handleSelectThread}
        onHoverThread={this.handleHoverThread}
        onToggleExpand={this.handleToggleExpand}
        onOpenMenu={this.handleOpenMenu}
        onEdit={this.handleEdit}
        onDelete={this.handleDelete}
        onEditChange={this.handleEditChange}
        onSubmitEdit={this.submitEdit}
        onCancelEdit={(): void => this.setState({ editingCommentId: "", draftEdit: "" })}
        onStartReply={(key: string): void =>
          this.setState((prev: ICommentsPanelState) => ({
            replyingThreadId: key,
            draftReply: "",
            expandedThreadIds: prev.expandedThreadIds.includes(key)
              ? prev.expandedThreadIds
              : [...prev.expandedThreadIds, key]
          }))
        }
        onReplyChange={this.handleReplyChange}
        onSubmitReply={this.submitReply}
        onCancelReply={(): void => this.setState({ replyingThreadId: "", draftReply: "" })}
        onResolve={this.handleResolve}
        onReopen={this.handleReopen}
        onToggleReaction={this.handleToggleReaction}
      />
    );

    // Content for the canvas pin popover: the pinned thread's card, shown regardless of the active
    // panel filter (a pin exists for every component thread, even filtered-out ones).
    const renderThreadPopover = (tKey: string): React.ReactNode => {
      const thread: IComments | undefined = threads.find((t: IComments) => threadKey(t) === tKey);
      if (!thread) return null;
      return renderThreadCard(this.buildViewModel(thread));
    };

    const tabLabel = (text: string, n: number, tone?: string): React.ReactNode => (
      <span className="comments-panel__tab">
        {text}
        {n > 0 && (
          <span className={`comments-panel__tab-count${tone ? ` comments-panel__tab-count--${tone}` : ""}`}>{n}</span>
        )}
      </span>
    );
    const filterOptions = [
      { value: CommentFilter.All, label: tabLabel("All", counts.all, "all"), dataTestId: "commentFilterTabAll" },
      { value: CommentFilter.Open, label: tabLabel("Open", counts.open, "open"), dataTestId: "commentFilterTabOpen" },
      {
        value: CommentFilter.Resolved,
        label: tabLabel("Resolved", counts.resolved, "resolved"),
        dataTestId: "commentFilterTabResolved"
      },
      { value: CommentFilter.Mentions, label: tabLabel("@", counts.mentions), dataTestId: "commentFilterTabMentions" }
    ];

    // The list under one filter tab. Drawn from its own filter so that, while the tabs swap, the
    // leaving pane is still the list it was.
    const renderListBody = (paneFilter: CommentFilter): React.ReactNode => {
      const paneViewModels: IThreadViewModel[] = paneFilter === filter ? viewModels : this.buildViewModels(paneFilter);
      if (isLoading) {
        // A thread list is coming, and its shape is known: an avatar and a line or two of text per
        // row. It draws that shape while it waits, instead of the word "Loading…" on an empty pane.
        return (
          <div className="comments-panel__loading" role="status" aria-busy="true" aria-label="Loading comments">
            <KuikaSkeletonList rows={4} lines={2} density="comfortable" />
          </div>
        );
      }
      if (hasError) {
        return (
          <div className="comments-panel__empty">
            <span className="comments-panel__empty-icon comments-panel__empty-icon--error">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4.5" />
                <path d="M12 16h0.01" />
              </svg>
            </span>
            <span className="comments-panel__empty-title">Comments could not be loaded</span>
            <button
              className="comments-panel__footer-btn comments-panel__error-retry"
              type="button"
              onClick={(): void => {
                void this.reloadComments();
              }}
            >
              Retry
            </button>
          </div>
        );
      }
      if (paneViewModels.length === 0 && query) {
        return (
          <div className="comments-panel__empty">
            <span className="comments-panel__empty-title">No results found</span>
          </div>
        );
      }
      if (paneViewModels.length === 0 && paneFilter !== CommentFilter.All) {
        return (
          <div className="comments-panel__empty">
            <span className="comments-panel__empty-title">No comments in this filter</span>
          </div>
        );
      }
      if (paneViewModels.length === 0) {
        return (
          <div className="comments-panel__empty">
            <span className="comments-panel__empty-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </span>
            <span className="comments-panel__empty-title">No comments yet</span>
            <span className="comments-panel__empty-hint">Click a component on the canvas to start a thread.</span>
          </div>
        );
      }
      return paneViewModels.map(renderThreadCard);
    };

    return (
      <div className="comments-panel" data-testid="comments-panel">
        {/* Header */}
        <div className="comments-panel__header">
          <span className="comments-panel__title">Comments</span>
        </div>

        {/* Filter tabs */}
        <div className="comments-panel__filters">
          <KuikaTabs options={filterOptions} value={filter} onChange={this.handleFilterChange} size="sm" />
        </div>

        {/* Search + sort */}
        <div className="comments-panel__toolbar">
          <div className="comments-panel__search">
            <KuikaInput
              type="text"
              placeholder="Search comments..."
              value={query}
              onChange={this.handleSearchChange}
              leftIcon={
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35" />
                </svg>
              }
              size="sm"
            />
          </div>
          <button
            className="comments-panel__sort-btn comments-panel__footer-btn"
            onClick={this.toggleSort}
            type="button"
            title={sort === CommentSort.Newest ? "Newest first" : "Oldest first"}
          >
            {sort === CommentSort.Newest ? "Newest" : "Oldest"}
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={{ marginLeft: 3 }}
            >
              {sort === CommentSort.Newest ? <path d="M12 5l7 7H5z" /> : <path d="M12 19l7-7H5z" />}
            </svg>
          </button>
        </div>

        {/* Thread list */}
        <div className="comments-panel__list">
          <KuikaTabContent
            activeKey={filter}
            order={COMMENT_FILTER_ORDER}
            render={(paneFilter: string): React.ReactNode => renderListBody(paneFilter as CommentFilter)}
          />
        </div>

        {/* Composer */}
        {this.renderComposer()}

        {/* Canvas pins overlay — portal to document.body, positioned via iframe rects.
            Also owns the comment-mode click-to-add inline composer. */}
        <CanvasCommentPins
          threads={threads}
          pinNumbers={pinNumbers}
          selectedThreadId={this.state.selectedThreadId}
          onSelectThread={this.handleSelectThread}
          onAddComment={this.handleCanvasAddComment}
          getComponentName={this.getComponentName}
          renderThreadPopover={renderThreadPopover}
        />
      </div>
    );
  }
}

// ─── HOC wrap (mirror comments-window export pattern) ─────────────────────────

const commentsPanel = withContext<ICommentsPanelProps>(withSD<ICommentsPanelProps>(CommentsPanel));
export { commentsPanel as CommentsPanel };
